"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAuthHeader } from '../utils/auth';
import { useOutletContext } from 'react-router-dom';
import { isEnglishOperatorMode } from '../utils/operatorMode';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
    Plus, Play, Trash2, GripVertical, Copy, Wand2, 
    Eye, Save, FileText, Download, X, Sparkles, Pencil,
    BarChart3, Users, Building2, GraduationCap, Briefcase, Link2,
    Image as ImageIcon, Upload, CheckCircle2, Eraser, AlertTriangle, Bold,
    ZoomIn, ZoomOut, Maximize
} from 'lucide-react';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const PUBLISH_DOMAIN = "https://sb-trk.site";

const GEM_URLS = {
    STRATEGIST: "https://gemini.google.com/gem/1sQpKse_udg2U-KEyvAbbR4uttB3IPsKL?usp=sharing",
    CRAFTSMAN: "https://gemini.google.com/gem/1hi4okk--eb5ypQvnd22KN6YzuqLczysf?usp=sharing",
    FIXER: "https://gemini.google.com/gem/14_-ASlAiw_w43BaFN9xMu44eqBYqlLYJ?usp=sharing"
};

const CATEGORIES = [
    { id: 'sales', label: '営業資料', icon: Briefcase, desc: '課題解決、事例、価格を重視' },
    { id: 'external', label: 'プレゼン（社外）', icon: Users, desc: '共感、ビジョン、市場性を重視' },
    { id: 'internal', label: 'プレゼン（社内）', icon: Building2, desc: 'ROI、リスク、工程を重視' },
    { id: 'simulation', label: 'シミュレーション', icon: BarChart3, desc: '数値比較、投資回収を重視' },
    { id: 'bank_plan', label: '銀行事業計画', icon: FileText, desc: '資金使途、返済の確実性を重視' },
    { id: 'seminar', label: 'セミナー資料', icon: GraduationCap, desc: '教育的価値、バックエンド誘導' },
];

const CATEGORY_COPY_EN = {
    sales: { label: 'Sales deck', desc: 'Problem, use case, and pricing focused' },
    external: { label: 'External presentation', desc: 'Narrative, market, and vision focused' },
    internal: { label: 'Internal presentation', desc: 'ROI, risk, and execution focused' },
    simulation: { label: 'Simulation', desc: 'Comparison, numbers, and payback focused' },
    bank_plan: { label: 'Bank business plan', desc: 'Use of funds and repayment focused' },
    seminar: { label: 'Seminar deck', desc: 'Educational value and follow-up focused' },
};

const getPresentationCategories = (isEnglishBeta = false) =>
    isEnglishBeta
        ? CATEGORIES.map(category => ({
            ...category,
            label: CATEGORY_COPY_EN[category.id]?.label || category.label,
            desc: CATEGORY_COPY_EN[category.id]?.desc || category.desc
        }))
        : CATEGORIES;

const PRESENTATION_COPY = {
    ja: {
        autoTitlePrefix: '新規資料',
        titleRequired: 'タイトルとURLスラッグは必須です。',
        freePlanLimit: '無料プラン制限です。',
        saveFailed: '保存に失敗しました。',
        paidOnly: '有料プラン限定です。',
        duplicateFailed: '複製失敗',
        deleteDocConfirm: '完全に削除しますか？',
        deleteFailed: '削除失敗',
        titleRequiredShort: 'タイトルを入力してください。',
        titleSaveFailed: 'タイトルの保存に失敗しました。',
        addFailed: '追加失敗',
        deletePageConfirm: '削除しますか？',
        pdfServerError: 'サーバー側でPDF生成中にエラーが発生しました。',
        emptyPdf: '生成されたファイルが空です。',
        pageTitle: 'プレゼンテーション資料',
        subtitle: 'AI-Powered Slide Deck Engine',
        aiCreate: 'AIに相談して作成',
        blankCreate: '空の資料を作成',
        loading: '読み込み中...',
        emptyTitle: '資料がまだありません',
        emptyDescription: 'AIに相談して作るか、空の資料からすぐに始められます。',
        modalTitle: 'AIに相談して作成',
        modalDescription: '最低限の情報だけ入れて、構成相談用の指示文を作ります。',
        titleLabel: 'タイトル',
        titlePlaceholder: '例: A社向け資料',
        slugLabel: 'URLスラッグ',
        targetUrlLabel: '相談したい相手・参考URL',
        aiNotesLabel: 'AIに伝えたいこと',
        aiNotesPlaceholder: 'ターゲット、目的、提案したいことを箇条書きで...',
        categoryLabel: '用途を選択',
        creating: '作成中...',
        createConsultation: '相談用プロジェクトを作成',
        project: 'Project',
        saveTitle: 'タイトルを保存',
        cancel: 'キャンセル',
        editTitle: 'タイトルを編集',
        slide: 'スライド',
        previewDeck: '資料をプレビュー',
        previewNote: '※ブラウザ版はズレて表示される可能性があります',
        generating: '生成中...',
        exportPdf: '高品質PDFを出力',
        backToList: 'プロジェクト一覧へ',
        viewingPage: 'VIEWING PAGE',
        bold: '太字',
        sizeDefault: 'サイズ: 標準',
        sizeTiny: '極小',
        sizeSmall: '小',
        sizeLarge: '大',
        sizeXLarge: '特大',
        sizeHuge: '巨大',
        sizeMax: '最大',
        saving: '保存中...',
        save: '保存',
        media: '素材管理',
        correctionAi: '修正AI',
        generateAi: '生成AI',
        firstAi: '壁打ちAI 最初にクリック',
        codePlaceholder: '職人Gemから生成されたコードをここにペースト...',
        chartEditorTitle: 'グラフ・表データの高度な編集',
        xAxisEditor: '横軸（カテゴリ名）の編集',
        itemPlaceholder: '項目名',
        syncSuccess: 'データの同期に成功しました。',
        syncNote: '職人AIが計算式（.mapなど）を使っている数値は自動解析の対象外となります。その場合はCODEモードで直接修正してください。',
        visualTitle: 'ビジュアル編集モード',
        visualDescription: 'プレビュー内の文字を直接クリックして編集したり、装飾ツールバーで色やサイズを調整できます。',
        visualWarning: '編集内容は「保存」ボタンを押すまで確定されません',
        validationMixedPages: "⚠️ 他のスライドのコードが混ざっている可能性があります。自分のページ以外の <div id='page-X'> を削除してください。",
        validationChartId: (chartId, pageNum) => `⚠️ グラフID (chart-${chartId}) がこのページ番号 (${pageNum}) と一致していません。`,
        pdfValidationContinue: 'ページ番号やグラフIDのズレが見つかりました。このままPDF出力しますか？',
        pdfValidationMore: (count) => `他 ${count} 件`,
    },
    en: {
        autoTitlePrefix: 'New presentation',
        titleRequired: 'Title and URL slug are required.',
        freePlanLimit: 'Free plan limit reached.',
        saveFailed: 'Save failed.',
        paidOnly: 'Available on paid plans only.',
        duplicateFailed: 'Duplicate failed.',
        deleteDocConfirm: 'Delete this presentation permanently?',
        deleteFailed: 'Delete failed.',
        titleRequiredShort: 'Enter a title.',
        titleSaveFailed: 'Could not save the title.',
        addFailed: 'Could not add a slide.',
        deletePageConfirm: 'Delete this slide?',
        pdfServerError: 'The server failed while generating the PDF.',
        emptyPdf: 'The generated file was empty.',
        pageTitle: 'Presentations',
        subtitle: 'Operator-ready slide workspace',
        aiCreate: 'Consult AI to create',
        blankCreate: 'New presentation',
        loading: 'Loading...',
        emptyTitle: 'No presentations yet',
        emptyDescription: 'Start with a blank presentation and add content manually or with your operator workflow.',
        modalTitle: 'Create with AI consultation',
        modalDescription: 'Add minimal context to generate a consultation prompt.',
        titleLabel: 'Title',
        titlePlaceholder: 'Example: Proposal for Acme',
        slugLabel: 'URL slug',
        targetUrlLabel: 'Target or reference URL',
        aiNotesLabel: 'Notes for AI',
        aiNotesPlaceholder: 'Target, objective, and key proposal points...',
        categoryLabel: 'Use case',
        creating: 'Creating...',
        createConsultation: 'Create consultation project',
        project: 'Project',
        saveTitle: 'Save title',
        cancel: 'Cancel',
        editTitle: 'Edit title',
        slide: 'Slide',
        previewDeck: 'Preview',
        previewNote: 'Browser preview may differ from PDF output',
        generating: 'Generating...',
        exportPdf: 'Export PDF',
        backToList: 'Back to presentations',
        viewingPage: 'VIEWING PAGE',
        bold: 'Bold',
        sizeDefault: 'Size: normal',
        sizeTiny: 'Tiny',
        sizeSmall: 'Small',
        sizeLarge: 'Large',
        sizeXLarge: 'Extra large',
        sizeHuge: 'Huge',
        sizeMax: 'Maximum',
        saving: 'Saving...',
        save: 'Save',
        media: 'Media',
        correctionAi: 'Correction AI',
        generateAi: 'Generate AI',
        firstAi: 'Consult AI first',
        codePlaceholder: 'Paste slide HTML here...',
        chartEditorTitle: 'Advanced chart and table data editor',
        xAxisEditor: 'Edit x-axis labels',
        itemPlaceholder: 'Item name',
        syncSuccess: 'Data synced.',
        syncNote: 'Numbers generated through formulas such as .map are not auto-detected. Edit them directly in CODE mode if needed.',
        visualTitle: 'Visual edit mode',
        visualDescription: 'Click text inside the preview to edit it, then adjust color and size with the toolbar.',
        visualWarning: 'Changes are not finalized until you press Save',
        validationMixedPages: "⚠️ Code from another slide may be mixed in. Remove <div id='page-X'> blocks that do not belong to this slide.",
        validationChartId: (chartId, pageNum) => `⚠️ Chart ID (chart-${chartId}) does not match this page number (${pageNum}).`,
        pdfValidationContinue: 'Page number or chart ID mismatches were found. Export the PDF anyway?',
        pdfValidationMore: (count) => `and ${count} more`,
    }
};

const EMPTY_DOC_FORM = {
    title: '',
    slug: '',
    category: 'sales',
    target_url: '',
    proposal_points: ''
};

const pad2 = (value) => String(value).padStart(2, '0');

const buildAutoDocForm = (docs = [], isEnglishBeta = false) => {
    const copy = isEnglishBeta ? PRESENTATION_COPY.en : PRESENTATION_COPY.ja;
    const now = new Date();
    const dateLabel = `${now.getFullYear()}/${pad2(now.getMonth() + 1)}/${pad2(now.getDate())} ${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
    const slugBase = `deck-${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}-${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}`;
    const usedSlugs = new Set(docs.map(doc => doc.slug).filter(Boolean));
    let slug = slugBase;
    let suffix = 2;

    while (usedSlugs.has(slug)) {
        slug = `${slugBase}-${suffix}`;
        suffix += 1;
    }

    return {
        ...EMPTY_DOC_FORM,
        title: `${copy.autoTitlePrefix} ${dateLabel}`,
        slug
    };
};

const readDocMetadata = (doc) => {
    if (!doc?.metadata) return {};
    if (typeof doc.metadata === 'string') {
        try {
            return JSON.parse(doc.metadata);
        } catch (e) {
            return {};
        }
    }
    return doc.metadata;
};

const validateHtmlContent = (html, pageNum, isEnglishBeta = false, options = {}) => {
    if (!html) return null;
    const copy = isEnglishBeta ? PRESENTATION_COPY.en : PRESENTATION_COPY.ja;
    const { strict = false } = options;
    
    const errors = [];

    // 1. 他のページIDが紛れ込んでいないかチェック
    const pageIdMatches = html.match(/id=["']page-(\d+)["']/g);
    if (pageIdMatches && pageIdMatches.length > 1) {
        errors.push(copy.validationMixedPages);
    }

    // 2. グラフIDが現在のページ番号と一致しているか
    const chartIdMatch = html.match(/id=["']chart-(\d+)["']/);
    if (strict && chartIdMatch && parseInt(chartIdMatch[1]) !== pageNum) {
        errors.push(copy.validationChartId(chartIdMatch[1], pageNum));
    }

    return errors.length > 0 ? errors : null;
};

// --- 🖼️ 素材管理モーダル ---
function PresentationMediaModal({ docId, images, onRefresh, onClose, isEnglishBeta = false }) {
    const copy = isEnglishBeta ? {
        title: 'Media library',
        uploadFailed: 'Upload failed.',
        deleteConfirm: 'Delete this image?',
        deleteFailed: 'Delete failed.',
        upload: 'Upload image',
        copied: 'Copied',
        copyPath: 'Copy path',
        delete: 'Delete'
    } : {
        title: '素材管理ボックス',
        uploadFailed: 'アップロード失敗',
        deleteConfirm: 'この画像を削除しますか？',
        deleteFailed: '削除失敗',
        upload: '画像をアップ',
        copied: 'コピー済',
        copyPath: 'パスをコピー',
        delete: '削除'
    };
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [copyFeedback, setCopyFeedback] = useState("");
    const buildImageUrl = (imageId) => `${API_URL}/api/docs/${docId}/images/${imageId}`;

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch(`${API_URL}/api/docs/${docId}/images`, {
                method: 'POST',
                headers: { 'Authorization': getAuthHeader().Authorization },
                body: formData
            });
            if (res.ok) onRefresh();
        } catch (err) { alert(copy.uploadFailed); }
        finally { setIsUploading(false); }
    };

    const handleDelete = async (imageId) => {
        if (!window.confirm(copy.deleteConfirm)) return;
        try {
            await fetch(`${API_URL}/api/docs/${docId}/images/${imageId}`, {
                method: 'DELETE',
                headers: getAuthHeader()
            });
            onRefresh();
        } catch (err) { alert(copy.deleteFailed); }
    };

    const copyToClipboard = (filename) => {
        navigator.clipboard.writeText(`img/${filename}`);
        setCopyFeedback(filename);
        setTimeout(() => setCopyFeedback(""), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-4xl rounded-3xl sm:rounded-[3rem] shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                <div className="p-5 sm:p-8 border-b flex justify-between items-center gap-3">
                    <h3 className="text-lg sm:text-xl font-black flex items-center gap-3"><ImageIcon className="text-blue-500"/> {copy.title}</h3>
                    <button onClick={onClose} className="text-slate-300 hover:text-slate-600 text-4xl">&times;</button>
                </div>
                
                <div className="p-5 sm:p-10 overflow-y-auto max-h-[70vh] sm:max-h-[60vh]">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        <div 
                            onClick={() => fileInputRef.current?.click()}
                            className="aspect-square border-4 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group"
                        >
                            {isUploading ? <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /> : (
                                <>
                                    <Upload className="text-slate-300 group-hover:text-blue-500 mb-2" size={32}/>
                                    <span className="text-[10px] font-black text-slate-400 group-hover:text-blue-600">{copy.upload}</span>
                                </>
                            )}
                            <input type="file" ref={fileInputRef} className="hidden" onChange={handleUpload} accept="image/*" />
                        </div>

                        {images.map((img, idx) => (
                            <div key={idx} className="group relative aspect-square bg-slate-50 rounded-[2rem] overflow-hidden border border-slate-100 hover:shadow-xl transition-all">
                                <img 
                                    src={buildImageUrl(img.id)} 
                                    className="w-full h-full object-cover" 
                                    alt={img.filename} 
                                />
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center p-4 gap-2">
                                    <button 
                                        onClick={() => copyToClipboard(img.filename)}
                                        className="w-full py-2 bg-white text-slate-800 rounded-full text-[10px] font-black flex items-center justify-center gap-1 hover:bg-blue-500 hover:text-white transition-all"
                                    >
                                        {copyFeedback === img.filename ? <><CheckCircle2 size={12}/> {copy.copied}</> : <><Copy size={12}/> {copy.copyPath}</>}
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(img.id)}
                                        className="w-full py-2 bg-red-500 text-white rounded-full text-[10px] font-black flex items-center justify-center gap-1 hover:bg-red-600"
                                    >
                                        <Trash2 size={12}/> {copy.delete}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- 🧠 プロンプト生成アシスタントモーダル ---
function PresentationPromptModal({ activeDoc, businessInfo, images, onClose }) {
    const [prompt, setPrompt] = useState("");
    const [isCopied, setIsCopied] = useState(false);

    const generatePrompt = () => {
        const imageList = images.length > 0 ? images.map(img => `- img/${img.filename}`).join('\n') : "なし";
        const categoryLabel = CATEGORIES.find(c => c.id === activeDoc.category)?.label || "営業資料";
        const metadata = readDocMetadata(activeDoc);

        const text = `
あなたはBtoB資料構成のスペシャリストです。
以下の情報に基づき、「${categoryLabel}」の構成案と制作プロンプトを作成してください。

# 1. クライアントの商材情報
${businessInfo || "（未登録：商材概要を教えてください）"}

# 2. ターゲット企業情報
- 調査対象URL: ${metadata.target_url || "なし"}
- 今回の提案の核: ${metadata.proposal_points || "なし"}

# 3. 利用可能な画像素材 (img/ファイル名 で使用可能)
${imageList}

# 4. 依頼内容
- まず [Google Search Tool] を使用し、「調査対象URL」の企業の状況を徹底調査してください。
- 調査結果に基づき、今回の目的に最適な【構成および枚数】を理由と共に提案してください。
- 構成合意後、1ページずつ職人AIへのプロンプトを作成してください。
- 各プロンプトの末尾には必ず以下の【制作制約】を付与してください：
  「A4横サイズ(1122x793)、背景白、アニメ禁止、日本語フォント、ページ番号は {{page_num}} / {{total_pages}}、グラフIDは chart-{{page_num}}、画像は img/ファイル名 形式」
`.trim();
        setPrompt(text);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(prompt);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
            window.open(GEM_URLS.STRATEGIST, '_blank');
        }, 1500);
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-2xl rounded-3xl sm:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <div className="p-5 sm:p-8 bg-indigo-600 text-white flex justify-between items-center gap-3">
                    <h3 className="text-lg sm:text-xl font-black flex items-center gap-3"><Sparkles/> 資料構成アシスタント</h3>
                    <button onClick={onClose} className="text-white/50 hover:text-white transition-colors"><X size={32}/></button>
                </div>
                <div className="p-5 sm:p-10 space-y-6">
                    {!prompt ? (
                        <div className="text-center space-y-6">
                            <p className="text-sm text-gray-500 leading-relaxed">商材とターゲットを統合し、最適な構成をAIに考えさせます。</p>
                            <button onClick={generatePrompt} className="w-full bg-indigo-600 text-white font-black py-5 rounded-full shadow-xl hover:bg-indigo-700 transition-all">指示文を生成する</button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <textarea readOnly className="w-full h-64 p-6 bg-gray-900 text-indigo-300 font-mono text-xs rounded-[2rem] leading-relaxed outline-none" value={prompt} />
                            <button onClick={handleCopy} className="w-full py-5 bg-indigo-600 text-white rounded-full font-black shadow-xl hover:bg-indigo-700 transition-all">
                                {isCopied ? "✅ コピーしました！Gemを開きます..." : "コピーして軍師Gemを開く"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CorrectionPromptModal({ currentHtml, onClose }) {
    const [instruction, setInstruction] = useState("");
    const [prompt, setPrompt] = useState("");
    const [isCopied, setIsCopied] = useState(false);

    const generatePrompt = () => {
        const text = `
あなたは修正専門の職人AIです。以下の「現在のコード」に対し、その下の「修正依頼」を反映してください。

# 1. 現在のコード
\`\`\`html
${currentHtml}
\`\`\`

# 2. 修正依頼
${instruction}

# 3. 制約
- レイアウトを壊さず、指示された箇所のみを正確に直してください。
- ページ番号 {{page_num}} やグラフID chart-{{page_num}} は絶対に変更しないでください。
`.trim();
        setPrompt(text);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(prompt);
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
            window.open(GEM_URLS.FIXER, '_blank');
        }, 1500);
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-2xl rounded-3xl sm:rounded-[3rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <div className="p-5 sm:p-8 bg-slate-800 text-white flex justify-between items-center gap-3">
                    <h3 className="text-lg sm:text-xl font-black flex items-center gap-3"><Eraser size={20}/> 修正AIアシスタント</h3>
                    <button onClick={onClose} className="text-white/50 hover:text-white transition-colors"><X size={32}/></button>
                </div>
                <div className="p-5 sm:p-10 space-y-6">
                    {!prompt ? (
                        <div className="space-y-6">
                            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest">どのように修正しますか？</label>
                            <textarea 
                                className="w-full h-40 p-5 bg-slate-50 border-2 border-slate-100 rounded-3xl text-sm outline-none focus:border-blue-500"
                                placeholder="例：代表者名を〇〇から大西に変えて。グラフの色を赤系にして。"
                                value={instruction}
                                onChange={e => setInstruction(e.target.value)}
                            />
                            <button onClick={generatePrompt} disabled={!instruction} className="w-full bg-slate-800 text-white font-black py-5 rounded-full shadow-xl hover:bg-black transition-all">修正用プロンプトを生成</button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <textarea readOnly className="w-full h-64 p-6 bg-gray-900 text-slate-300 font-mono text-xs rounded-[2rem] leading-relaxed outline-none" value={prompt} />
                            <button onClick={handleCopy} className="w-full py-5 bg-blue-600 text-white rounded-full font-black shadow-xl hover:bg-blue-700 transition-all">
                                {isCopied ? "✅ コピーしました！修正Gemを開きます..." : "コピーして修正Gemを開く"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- 📄 メインページコンポーネント ---
export default function MarketingPresentationPage() {
    const { user } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = isEnglishBeta ? PRESENTATION_COPY.en : PRESENTATION_COPY.ja;
    const categories = useMemo(() => getPresentationCategories(isEnglishBeta), [isEnglishBeta]);
    const clientId = useMemo(() => (
      user?.role === "admin" || user?.role === "admin_staff" 
        ? localStorage.getItem("selectedClientId") || "" 
        : user?.client_id
    ), [user]);

    const [docs, setDocs] = useState([]);
    const [activeDoc, setActiveDoc] = useState(null);
    const [isEditingDocTitle, setIsEditingDocTitle] = useState(false);
    const [docTitleDraft, setDocTitleDraft] = useState('');
    const [isUpdatingDocTitle, setIsUpdatingDocTitle] = useState(false);
    const [pages, setPages] = useState([]);
    const [selectedPageIndex, setSelectedPageIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFreePlan, setIsFreePlan] = useState(false);
    
    // モーダル管理
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    
    const [businessInfo, setBusinessInfo] = useState("");
    const [images, setImages] = useState([]);
    const [newDocForm, setNewDocForm] = useState({ ...EMPTY_DOC_FORM });
    const [isCreatingDoc, setIsCreatingDoc] = useState(false);
    const [showCorrectionModal, setShowCorrectionModal] = useState(false);
    const [editMode, setEditMode] = useState('code');
    const [history, setHistory] = useState([]);
    const isInternalUpdate = useRef(false);
    const [extractedChartData, setExtractedChartData] = useState(null);

    // --- 🚀 プレビュー用スケーリング管理 ---
    const [previewScale, setPreviewScale] = useState(0.5);
    const previewContainerRef = useRef(null);

    useEffect(() => {
        const handleUndo = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                if (history.length > 0) {
                    e.preventDefault();
                    const prevState = history[history.length - 1];
                    setPages(prevState);
                    setHistory(prev => prev.slice(0, -1));
                }
            }
        };
        window.addEventListener('keydown', handleUndo);
        const handleMessage = (event) => {
            if (event.data.type === 'PREVIEW_EDIT') {
                isInternalUpdate.current = true;
                setHistory(prev => [...prev.slice(-29), pages]);
                const newPages = [...pages];
                const currentPage = newPages[selectedPageIndex];
                if (currentPage) {
                    const oldHtml = currentPage.raw_html;
                    let newInnerHtml = event.data.html;

                    // 🚀 対策1: Chart.jsが勝手に付けた不要なstyleや属性を掃除（正規表現）
                    newInnerHtml = newInnerHtml.replace(/style="[^"]*height:[^"]*"/g, '');
                    newInnerHtml = newInnerHtml.replace(/width="\d+"/g, '').replace(/height="\d+"/g, '');

                    // 🚀 対策2: {{page_num}} が数字に化けるのを防ぐ（簡易版）
                    // 表示されている数字を再び変数タグに戻す処理（※数字が他で使われていると誤爆するので注意）
                    const pageNum = selectedPageIndex + 1;
                    const totalPages = pages.length;
                    newInnerHtml = newInnerHtml.replace(new RegExp(`\\b${pageNum} / ${totalPages}\\b`, 'g'), '{{page_num}} / {{total_pages}}');

                    // 🚀 対策3: 元のHTMLの <head> や <script> を維持して body だけ差し替える
                    const bodyMatch = oldHtml.match(/([\s\S]*<body[^>]*>)([\s\S]*)(<\/body>[\s\S]*)/i);
                    if (bodyMatch) {
                        // <body> タグの中身だけを入れ替えて完成！
                        currentPage.raw_html = `${bodyMatch[1]}\n${newInnerHtml}\n${bodyMatch[3]}`;
                    } else {
                        // <body>がない場合（canvas直書きなど）のフォールバック
                        currentPage.raw_html = newInnerHtml;
                    }
                    setPages(newPages);
                }
                setTimeout(() => { isInternalUpdate.current = false; }, 100);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('keydown', handleUndo);
            window.removeEventListener('message', handleMessage);
        };
    }, [pages, selectedPageIndex, history]);

    const updatePreviewScale = useCallback(() => {
        if (!previewContainerRef.current) return;
        const container = previewContainerRef.current;
        const availableWidth = container.offsetWidth - 80;
        const availableHeight = container.offsetHeight - 80;
        const scaleW = availableWidth / 1122;
        const scaleH = availableHeight / 793;
        setPreviewScale(Math.min(scaleW, scaleH, 1));
    }, []);

    useEffect(() => {
        if (activeDoc) {
            updatePreviewScale();
            window.addEventListener('resize', updatePreviewScale);
        }
        return () => window.removeEventListener('resize', updatePreviewScale);
    }, [activeDoc, updatePreviewScale]);

    // --- 🚀 HTMLパース（画像パス置換 & 変数反映） ---
    const imageIdByFilename = useMemo(() => {
        const map = new Map();
        images.forEach((image) => {
            if (image?.filename && image?.id) map.set(image.filename, image.id);
        });
        return map;
    }, [images]);

    const getFinalHtml = useCallback((rawHtml, docId, pageNum, totalPages) => {
        if (!rawHtml) return "";
        let html = rawHtml.replace(/{{page_num}}/g, pageNum).replace(/{{total_pages}}/g, totalPages);
        const imageUrl = (filename) => {
            const imageId = imageIdByFilename.get(filename);
            return imageId ? `${API_URL}/api/docs/${docId}/images/${imageId}` : null;
        };
        const imgPattern = /=(["'])(\.?\/?img\/)([^"']+)\1/g;
        const cssUrlPattern = /url\((["']?)(\.?\/?img\/)([^"')]+)\1\)/g;
        return html
            .replace(imgPattern, (m, q, p, f) => {
                const url = imageUrl(f);
                return url ? `=${q}${url}${q}` : m;
            })
            .replace(cssUrlPattern, (m, q, p, f) => {
                const url = imageUrl(f);
                return url ? `url(${q}${url}${q})` : m;
            });
    }, [imageIdByFilename]);

    const fetchData = useCallback(async () => {
        if (!clientId) return;
        try {
            const [docsRes, bizRes, usageRes] = await Promise.all([
                fetch(`${API_URL}/api/docs?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/client-info?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/usage-status?clientId=${clientId}`, { headers: getAuthHeader() })
            ]);
            const dData = await docsRes.json();
            const bData = await bizRes.json();
            const uData = await usageRes.json();
            
            if (dData.success) setDocs(dData.docs);
            if (bData.success && bData.info) setBusinessInfo(bData.info.business_info || "");
            setIsFreePlan(uData.plan === 'free');
            setIsLoading(false);
        } catch (e) { console.error(e); }
    }, [clientId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const fetchPagesAndImages = useCallback(async (docId) => {
        try {
            const [pRes, imgRes] = await Promise.all([
                fetch(`${API_URL}/api/docs/${docId}/pages`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/docs/${docId}/images`, { headers: getAuthHeader() })
            ]);
            const pData = await pRes.json();
            const imgData = await imgRes.json();
            
            if (pData.success) setPages(pData.pages);
            if (imgData.success) setImages(imgData.images);
        } catch (e) { console.error(e); }
    }, [clientId]);

    const handleCreateDoc = async (formInput = newDocForm, options = {}) => {
        const docForm = {
            ...EMPTY_DOC_FORM,
            ...formInput,
            slug: (formInput.slug || '').replace(/[^a-z0-9-]/gi, '').toLowerCase()
        };

        if (!docForm.title || !docForm.slug) return alert(copy.titleRequired);
        if (isFreePlan && docs.length >= 1) return alert(copy.freePlanLimit);
        if (isCreatingDoc) return;

        setIsCreatingDoc(true);
        try {
            const res = await fetch(`${API_URL}/api/docs/save`, {
                method: 'POST',
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({ 
                    ...docForm, 
                    client_id: clientId,
                    metadata: { target_url: docForm.target_url, proposal_points: docForm.proposal_points }
                })
            });
            const data = await res.json();
            if (data.success) {
                setShowCreateModal(false);
                setNewDocForm({ ...EMPTY_DOC_FORM });
                await fetchData();
                handleSelectDoc({ id: data.id, ...docForm, metadata: { target_url: docForm.target_url, proposal_points: docForm.proposal_points } });
                if (options.openPrompt && !isEnglishBeta) setTimeout(() => setShowPromptModal(true), 0);
            } else {
                alert(data.error || copy.saveFailed);
            }
        } catch (e) { alert(copy.saveFailed); }
        finally { setIsCreatingDoc(false); }
    };

    const handleOpenAiCreate = () => {
        if (isFreePlan && docs.length >= 1) return alert(copy.freePlanLimit);
        setNewDocForm(buildAutoDocForm(docs, isEnglishBeta));
        setShowCreateModal(true);
    };

    const handleCreateBlankDoc = () => {
        handleCreateDoc(buildAutoDocForm(docs, isEnglishBeta));
    };

    const handleDuplicate = async (e, id) => {
        e.stopPropagation();
        if (isFreePlan) return alert(copy.paidOnly);
        try {
            const res = await fetch(`${API_URL}/api/docs/${id}/duplicate`, { method: 'POST', headers: getAuthHeader() });
            if (res.ok) fetchData();
        } catch (e) { alert(copy.duplicateFailed); }
    };

    const handleDeleteDoc = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm(copy.deleteDocConfirm)) return;
        try {
            await fetch(`${API_URL}/api/docs/${id}`, { method: 'DELETE', headers: getAuthHeader() });
            fetchData();
        } catch (e) { alert(copy.deleteFailed); }
    };

    const handleSelectDoc = (doc) => {
        setActiveDoc(doc);
        setDocTitleDraft(doc.title || '');
        setIsEditingDocTitle(false);
        fetchPagesAndImages(doc.id);
    };

    const handleSaveDocTitle = async () => {
        const nextTitle = docTitleDraft.trim();
        if (!activeDoc) return;
        if (!nextTitle) return alert(copy.titleRequiredShort);
        if (nextTitle === activeDoc.title) {
            setIsEditingDocTitle(false);
            return;
        }

        const updatedDoc = { ...activeDoc, title: nextTitle };
        setIsUpdatingDocTitle(true);
        try {
            const res = await fetch(`${API_URL}/api/docs/save`, {
                method: 'POST',
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({
                    id: activeDoc.id,
                    title: nextTitle,
                    slug: activeDoc.slug,
                    category: activeDoc.category || 'sales',
                    client_id: clientId,
                    metadata: readDocMetadata(activeDoc)
                })
            });
            const data = await res.json();
            if (data.success) {
                setActiveDoc(updatedDoc);
                setDocs(prev => prev.map(doc => doc.id === activeDoc.id ? { ...doc, title: nextTitle } : doc));
                setIsEditingDocTitle(false);
            } else {
                alert(data.error || copy.titleSaveFailed);
            }
        } catch (e) {
            alert(copy.titleSaveFailed);
        } finally {
            setIsUpdatingDocTitle(false);
        }
    };

    const handleCancelDocTitleEdit = () => {
        setDocTitleDraft(activeDoc?.title || '');
        setIsEditingDocTitle(false);
    };

    const handleManualSave = async () => {
        const currentPage = pages[selectedPageIndex];
        if (!activeDoc || !currentPage) return;
        await handleSavePageContent(currentPage.raw_html);
    };

    const handleZoomIn = () => setPreviewScale(prev => Math.min(prev + 0.1, 2.0));
    const handleZoomOut = () => setPreviewScale(prev => Math.max(prev - 0.1, 0.1));
    const handleZoomFit = () => updatePreviewScale();

    const parseChartData = useCallback(() => {
        const currentPage = pages[selectedPageIndex];
        if (!currentPage || !currentPage.raw_html) {
            setExtractedChartData(null);
            return;
        }

        const html = currentPage.raw_html;
        
        // 1. ラベル（横軸）の抽出
        const labelsMatch = html.match(/(?:labels|const\s+labels)\s*[:=]\s*\[([\s\S]*?)\]/);
        
        // 2. データセットの抽出（どんな形式でも data: [...] を見つける）
        const datasets = [];
        
        // パターンA: const salesData = [1, 2, 3] の形式
        const varRegex = /const\s+(\w+)Data\s*=\s*\[([\d\s,.]+)\]/g;
        let vMatch;
        while ((vMatch = varRegex.exec(html)) !== null) {
            const vals = vMatch[2].split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
            if (vals.length >= 2) {
                datasets.push({
                    fullMatch: vMatch[0],
                    label: vMatch[1].replace(/([A-Z])/g, ' $1').trim(),
                    dataContent: vMatch[2],
                    values: vals,
                    type: 'variable'
                });
            }
        }

        // パターンB: { data: [1, 2, 3] } または { label: '...', data: [...] }
        // 🚀 label がなくても拾えるように正規表現を緩和
        const dsRegex = /(?:label\s*:\s*['"](.*?)['"][\s\S]*?)?data\s*:\s*\[([\d\s,.]+)\]/g;
        let dsMatch;
        while ((dsMatch = dsRegex.exec(html)) !== null) {
            const vals = dsMatch[2].split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
            // 重複チェック
            if (vals.length >= 2 && !datasets.find(d => d.dataContent === dsMatch[2])) {
                datasets.push({
                    fullMatch: dsMatch[0],
                    label: dsMatch[1] || `Dataset ${datasets.length + 1}`, // labelがない場合は連番
                    dataContent: dsMatch[2],
                    values: vals,
                    type: 'object'
                });
            }
        }

        if (labelsMatch && datasets.length > 0) {
            const labels = labelsMatch[1].split(',').map(s => s.trim().replace(/^['"]|['"]$/g, '')).filter(s => s !== "");
            setExtractedChartData({
                labels,
                originalLabelsContent: labelsMatch[1],
                datasets
            });
        } else {
            setExtractedChartData(null);
        }
    }, [pages, selectedPageIndex]);

    const handleUpdateChartValue = (dsIdx, valIdx, newValue) => {
        if (!extractedChartData) return;
        const newPages = [...pages];
        const currentPage = newPages[selectedPageIndex];
        const ds = extractedChartData.datasets[dsIdx];
        const newValues = [...ds.values];
        newValues[valIdx] = parseFloat(newValue) || 0;
        const newContent = newValues.join(', ');
        
        currentPage.raw_html = currentPage.raw_html.replace(ds.fullMatch, ds.fullMatch.replace(ds.dataContent, newContent));
        setPages(newPages);
        parseChartData(); 
    };

    const handleUpdateDatasetLabel = (dsIdx, newLabel) => {
        if (!extractedChartData) return;
        const newPages = [...pages];
        const currentPage = newPages[selectedPageIndex];
        const ds = extractedChartData.datasets[dsIdx];
        
        let oldPart, newPart;
        if (ds.type === 'variable') {
            // salesData などの変数名自体を書き換えるのは危険なので、変数の場合はラベル変更は無視するか、
            // もしくはラベル表示のみ変える設定にする（今回は安全のため、オブジェクト形式のみ置換）
            return; 
        } else {
            oldPart = `label: '${ds.label}'`;
            newPart = `label: '${newLabel}'`;
        }
        
        currentPage.raw_html = currentPage.raw_html.replace(oldPart, newPart);
        setPages(newPages);
        parseChartData();
    };

    const handleUpdateXLabel = (lIdx, newLabel) => {
        if (!extractedChartData) return;
        const newPages = [...pages];
        const currentPage = newPages[selectedPageIndex];
        const newLabels = [...extractedChartData.labels];
        newLabels[lIdx] = newLabel;
        const newLabelsContent = newLabels.map(l => `'${l}'`).join(', ');
        currentPage.raw_html = currentPage.raw_html.replace(extractedChartData.originalLabelsContent, newLabelsContent);
        setPages(newPages);
        parseChartData();
    };

    useEffect(() => {
        parseChartData();
    }, [selectedPageIndex, parseChartData]);

    const handleSavePageContent = async (rawHtml) => {
        if (!activeDoc || !pages[selectedPageIndex]) return;
        setIsSaving(true);
        try {
            await fetch(`${API_URL}/api/docs/pages/save`, {
                method: 'POST',
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({
                    doc_id: activeDoc.id,
                    page_id: pages[selectedPageIndex].id,
                    order_index: selectedPageIndex,
                    raw_html: rawHtml
                })
            });
            fetchPagesAndImages(activeDoc.id);
        } catch (e) { console.error("Auto Save Failed"); }
        finally { setIsSaving(false); }
    };

    const handleAddPage = async () => {
        if (!activeDoc) return;
        try {
            const res = await fetch(`${API_URL}/api/docs/pages/save`, {
                method: 'POST',
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({ doc_id: activeDoc.id, order_index: pages.length, raw_html: "" })
            });
            if (res.ok) {
                await fetchPagesAndImages(activeDoc.id);
                setSelectedPageIndex(pages.length);
            }
        } catch (e) { alert(copy.addFailed); }
    };

    const handleDeletePage = async (e, pageId) => {
        e.stopPropagation();
        if (!window.confirm(copy.deletePageConfirm)) return;
        try {
            await fetch(`${API_URL}/api/docs/pages/${pageId}`, { method: 'DELETE', headers: getAuthHeader() });
            await fetchPagesAndImages(activeDoc.id);
            setSelectedPageIndex(0);
        } catch (err) { alert(copy.deleteFailed); }
    };

    const onDragEnd = async (result) => {
        if (!result.destination || !activeDoc) return;
        const newPages = Array.from(pages);
        const [reorderedItem] = newPages.splice(result.source.index, 1);
        newPages.splice(result.destination.index, 0, reorderedItem);
        setPages(newPages);
        setSelectedPageIndex(result.destination.index);
        const orders = newPages.map((p, index) => ({ id: p.id, index }));
        await fetch(`${API_URL}/api/docs/pages/reorder`, { method: 'POST', headers: { ...getAuthHeader(true) }, body: JSON.stringify({ doc_id: activeDoc.id, orders }) });
    };

    const selectedPage = pages[selectedPageIndex];
    const selectedRawHtml = selectedPage?.raw_html || "";
    const hasSelectedPage = Boolean(selectedPage);

    const memoizedIframe = useMemo(() => {
        if (!activeDoc || !hasSelectedPage) return null;

        return (
            <iframe 
                key={`${activeDoc.id}-${selectedPageIndex}-${editMode}`} 
                className="w-full h-full border-none" 
                srcDoc={`
                <html>
                    <head>
                        <script src="https://cdn.tailwindcss.com"></script>
                        <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
                        <script>
                            window.addEventListener('message', (e) => {
                                if (e.data.type === 'EXEC_COMMAND') {
                                    document.execCommand(e.data.command, false, e.data.value || null);
                                    window.parent.postMessage({type: 'PREVIEW_EDIT', html: document.getElementById('canvas').innerHTML}, '*');
                                }
                            });
                        </script>
                        <style>
                            body { margin: 0; background: #94a3b8; display: flex; justify-content: center; align-items: flex-start; padding: 20px; }
                            .canvas { background: white; width: 1122px; height: 793px; overflow: hidden; flex-shrink: 0; box-shadow: 0 0 40px rgba(0,0,0,0.3); position: relative; }
                            [contenteditable="true"]:focus { outline: 3px solid #3b82f6; outline-offset: -3px; }
                        </style>
                    </head>
                    <body>
                        <div id="canvas" class="canvas" contenteditable="${editMode === 'visual'}" 
                             onblur="window.parent.postMessage({type: 'PREVIEW_EDIT', html: this.innerHTML}, '*')">
                            ${getFinalHtml(selectedRawHtml, activeDoc.id, selectedPageIndex + 1, pages.length)}
                        </div>
                    </body>
                </html>
            `} title="Main Slide Preview" />
        );
    }, [activeDoc, selectedPageIndex, editMode, selectedRawHtml, hasSelectedPage, pages.length, getFinalHtml]);

    const handleExportPdf = async () => {
        if (!activeDoc) return;
        const strictWarnings = pages.flatMap((page, index) => {
            const warnings = validateHtmlContent(page.raw_html, index + 1, isEnglishBeta, { strict: true }) || [];
            return warnings.map(message => `${copy.slide} ${index + 1}: ${message}`);
        });

        if (strictWarnings.length > 0) {
            const warningPreview = strictWarnings.slice(0, 5).join('\n');
            const moreCount = strictWarnings.length > 5 ? `\n...${copy.pdfValidationMore(strictWarnings.length - 5)}` : '';
            if (!window.confirm(`${copy.pdfValidationContinue}\n\n${warningPreview}${moreCount}`)) return;
        }

        setIsSaving(true);
        try {
            const exportPayload = isEnglishBeta ? { operator_surface: 'english_beta' } : {};
            const res = await fetch(`${API_URL}/api/docs/${activeDoc.id}/export-pdf`, {
                method: 'POST',
                headers: getAuthHeader(true),
                body: JSON.stringify(exportPayload)
            });

            // 🚀 修正ポイント：レスポンスが「OK」でない場合は中身をテキストとして読み、アラートを出す
            if (!res.ok) {
                const errorJson = await res.json();
                throw new Error(errorJson.error || copy.pdfServerError);
            }

            // 正常な場合のみダウンロード処理
            const blob = await res.blob();
            if (blob.size === 0) throw new Error(copy.emptyPdf);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${activeDoc.title}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

        } catch (e) {
            console.error("PDF Export error:", e);
            alert(e.message); // 🚀 ここで本当のエラー原因が表示されます
        } finally {
            setIsSaving(false);
        }
    };

    if (!activeDoc) {
        return (
            <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-12">
                    <div>
                        <h1 className="text-2xl sm:text-4xl font-black text-slate-800 tracking-tight">{copy.pageTitle}</h1>
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-2">{copy.subtitle}</p>
                    </div>
                    <div className="grid w-full gap-3 sm:w-auto sm:flex sm:items-center sm:justify-end">
                        {!isEnglishBeta && (
                            <button onClick={handleOpenAiCreate} className="w-full sm:w-auto justify-center bg-blue-600 text-white px-8 sm:px-10 py-4 rounded-full font-black text-sm flex items-center gap-3 hover:bg-blue-700 shadow-xl transition-all disabled:bg-slate-200 disabled:shadow-none" disabled={isCreatingDoc}>
                                <Wand2 size={20} /> {copy.aiCreate}
                            </button>
                        )}
                        <button onClick={handleCreateBlankDoc} className={`w-full sm:w-auto justify-center px-8 sm:px-10 py-4 rounded-full font-black text-sm flex items-center gap-3 transition-all disabled:bg-slate-100 disabled:text-slate-300 ${isEnglishBeta ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-xl disabled:shadow-none' : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-sm'}`} disabled={isCreatingDoc}>
                            <Plus size={20} /> {copy.blankCreate}
                        </button>
                    </div>
                </div>

                {isLoading ? <div className="text-center py-20 text-slate-300 font-black tracking-widest animate-pulse">{copy.loading}</div> : (
                    docs.length === 0 ? (
                        <div className="py-20 text-center bg-white rounded-[2rem] border border-slate-100">
                            <FileText className="mx-auto text-slate-200 mb-5" size={48} />
                            <p className="text-slate-500 font-black">{copy.emptyTitle}</p>
                            <p className="text-xs text-slate-400 font-bold mt-2">{copy.emptyDescription}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
                            {docs.map(doc => {
                                const category = categories.find(c => c.id === doc.category);
                                const CategoryIcon = category?.icon || FileText;
                                return (
                                    <div key={doc.id} onClick={() => handleSelectDoc(doc)} className="bg-white p-5 sm:p-10 rounded-3xl sm:rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-2 cursor-pointer transition-all duration-500 group relative">
                                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-50 text-slate-400 rounded-2xl flex items-center justify-center mb-5 sm:mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all">
                                            <CategoryIcon size={28} />
                                        </div>
                                        <h3 className="font-black text-xl sm:text-2xl text-slate-800 mb-2 truncate">{doc.title}</h3>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                            <span className="text-[10px] font-black uppercase text-slate-300 tracking-widest break-all">/doc/{doc.slug}</span>
                                            <span className="text-[10px] font-black text-blue-400 uppercase">{category?.label || doc.category}</span>
                                        </div>
                                        <div className="absolute top-5 right-5 sm:top-8 sm:right-8 flex gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all">
                                            <button onClick={(e) => handleDuplicate(e, doc.id)} className="p-3 text-slate-200 hover:text-blue-500 hover:bg-blue-50 rounded-full"><Copy size={18}/></button>
                                            <button onClick={(e) => handleDeleteDoc(e, doc.id)} className="p-3 text-slate-200 hover:text-red-500 hover:bg-red-50 rounded-full"><Trash2 size={18}/></button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}

                {showCreateModal && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 backdrop-blur-md">
                        <div className="bg-white w-full max-w-5xl rounded-3xl sm:rounded-[3.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                            <div className="p-5 sm:p-10 border-b flex justify-between items-center gap-3 bg-white">
                                <div>
                                    <h3 className="text-xl sm:text-2xl font-black text-slate-800">{copy.modalTitle}</h3>
                                    <p className="text-xs text-slate-400 font-bold mt-2">{copy.modalDescription}</p>
                                </div>
                                <button onClick={() => setShowCreateModal(false)} className="text-slate-300 hover:text-slate-600 text-5xl">&times;</button>
                            </div>
                            <div className="p-5 sm:p-8 lg:p-12 overflow-y-auto max-h-[78vh] sm:max-h-[75vh] grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 scrollbar-hide">
                                <div className="space-y-6 sm:space-y-10">
                                    <div className="space-y-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{copy.titleLabel}</label>
                                        <input type="text" className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-3xl font-black text-xl focus:border-blue-500 focus:bg-white transition-all outline-none" value={newDocForm.title} onChange={e => setNewDocForm({...newDocForm, title: e.target.value})} placeholder={copy.titlePlaceholder} />
                                    </div>
                                    <div className="space-y-4">
                                        <label className="block text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-2"><Link2 size={12}/> {copy.slugLabel}</label>
                                        <div className="grid gap-2 sm:flex sm:items-center sm:gap-3">
                                            <span className="text-slate-300 font-bold font-mono">/doc/</span>
                                            <input type="text" className="flex-grow p-5 bg-blue-50/30 border-2 border-blue-100 rounded-3xl font-mono text-sm focus:border-blue-500 outline-none" value={newDocForm.slug} onChange={e => setNewDocForm({...newDocForm, slug: e.target.value.replace(/[^a-z0-9-]/gi, '').toLowerCase()})} placeholder="proposal-a" />
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{copy.targetUrlLabel}</label>
                                        <input type="text" className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-3xl text-sm outline-none" value={newDocForm.target_url} onChange={e => setNewDocForm({...newDocForm, target_url: e.target.value})} placeholder="https://..." />
                                    </div>
                                    <div className="space-y-4">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{copy.aiNotesLabel}</label>
                                        <textarea className="w-full p-5 bg-slate-50 border-2 border-transparent rounded-3xl text-sm h-32 outline-none resize-none" value={newDocForm.proposal_points} onChange={e => setNewDocForm({...newDocForm, proposal_points: e.target.value})} placeholder={copy.aiNotesPlaceholder}></textarea>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{copy.categoryLabel}</label>
                                    <div className="grid grid-cols-1 gap-3">
                                        {categories.map(cat => (
                                            <div key={cat.id} onClick={() => setNewDocForm({...newDocForm, category: cat.id})} className={`p-5 rounded-[2rem] border-2 cursor-pointer transition-all flex items-center gap-5 ${newDocForm.category === cat.id ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100' : 'border-slate-50 hover:border-slate-200'}`}>
                                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${newDocForm.category === cat.id ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-400'}`}><cat.icon size={24}/></div>
                                                <div>
                                                    <p className="text-sm font-black text-slate-800">{cat.label}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">{cat.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="p-5 sm:p-10 border-t bg-slate-50/50 flex justify-end">
                                <button onClick={() => handleCreateDoc(newDocForm, { openPrompt: true })} disabled={!newDocForm.title || !newDocForm.slug || isCreatingDoc} className="w-full sm:w-auto bg-blue-600 text-white px-10 sm:px-20 py-5 rounded-full font-black text-sm shadow-2xl hover:bg-blue-700 disabled:bg-slate-200 transition-all">
                                    {isCreatingDoc ? copy.creating : copy.createConsultation}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-slate-50 overflow-hidden -m-3 sm:-m-4 md:-m-8">
            <div className="h-[34vh] min-h-[260px] w-full lg:h-auto lg:w-80 bg-white border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col">
                <div className="p-5 border-b bg-slate-50/50">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{copy.project}</span>
                            {isEditingDocTitle ? (
                                <div className="mt-2 flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={docTitleDraft}
                                        onChange={(e) => setDocTitleDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveDocTitle();
                                            if (e.key === 'Escape') handleCancelDocTitleEdit();
                                        }}
                                        className="min-w-0 flex-1 rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-black text-slate-700 outline-none focus:border-blue-500"
                                        autoFocus
                                    />
                                    <button onClick={handleSaveDocTitle} disabled={isUpdatingDocTitle} className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-slate-200 transition-colors" title={copy.saveTitle}>
                                        <Save size={14}/>
                                    </button>
                                    <button onClick={handleCancelDocTitleEdit} disabled={isUpdatingDocTitle} className="p-2 bg-white text-slate-400 rounded-xl border border-slate-100 hover:text-slate-700 transition-colors" title={copy.cancel}>
                                        <X size={14}/>
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => {
                                        setDocTitleDraft(activeDoc.title || '');
                                        setIsEditingDocTitle(true);
                                    }}
                                    className="group mt-1 flex max-w-full items-center gap-2 text-left"
                                    title={copy.editTitle}
                                >
                                    <span className="truncate text-sm font-black text-slate-700 group-hover:text-blue-600 transition-colors">{activeDoc.title}</span>
                                    <Pencil size={13} className="flex-shrink-0 text-slate-300 group-hover:text-blue-500 transition-colors" />
                                </button>
                            )}
                        </div>
                        <button onClick={handleAddPage} className="p-2 hover:bg-blue-100 text-blue-600 rounded-xl transition-colors bg-white border border-blue-50"><Plus size={20}/></button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="slides">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                                    {pages.map((page, index) => (
                                        <Draggable key={page.id} draggableId={String(page.id)} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps} // 🚀 ここに移動！これで枠内どこでもドラッグ可能になります
                                                    onClick={() => setSelectedPageIndex(index)}
                                                    className={`group relative p-4 rounded-[2rem] border-2 transition-all cursor-grab active:cursor-grabbing ${selectedPageIndex === index ? 'border-blue-500 bg-blue-50/30 shadow-xl shadow-blue-50' : 'border-transparent hover:bg-slate-50'}`}
                                                >
                                                    {/* GripVerticalアイコンは、ドラッグできることの視覚的な目印として薄く残しておきます（ml-4を削除してスッキリ） */}
                                                    <div className="flex justify-between items-center mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <GripVertical size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{copy.slide} {index + 1}</span>
                                                        </div>
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation(); // ドラッグイベントとクリックイベントの衝突を防ぐ
                                                                handleDeletePage(e, page.id);
                                                            }} 
                                                            className="relative z-10 opacity-0 group-hover:opacity-100 text-red-300 hover:text-red-500 transition-all"
                                                        >
                                                            <Trash2 size={14}/>
                                                        </button>
                                                    </div>
                                                    
                                                    {/* サムネイルプレビュー */}
                                                    <div className="aspect-video bg-white rounded-2xl border border-slate-200 overflow-hidden relative shadow-inner pointer-events-none">
                                                        <div style={{ transform: 'scale(0.18)', transformOrigin: 'top left', width: '1122px', height: '793px' }}>
                                                            <iframe className="w-full h-full border-none" srcDoc={`<html><head><script src="https://cdn.tailwindcss.com"></script><script src="https://cdn.jsdelivr.net/npm/chart.js"></script></head><body style="margin:0; overflow:hidden;">${getFinalHtml(page.raw_html, activeDoc.id, index + 1, pages.length)}</body></html>`} title={`thumb-${index}`} />
                                                        </div>
                                                        {!page.raw_html && <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">No Content</div>}
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
                <div className="p-3 sm:p-4 lg:p-6 border-t bg-white grid grid-cols-2 gap-2 lg:block lg:space-y-3">
                    <button 
                        onClick={() => window.open(`${PUBLISH_DOMAIN}/doc/${activeDoc.slug}`, '_blank')} 
                        className="w-full py-3 lg:py-5 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex flex-col items-center justify-center gap-1 hover:bg-black shadow-xl transition-all"
                    >
                        <div className="flex items-center gap-3">
                            <Play size={16} />{copy.previewDeck}
                        </div>
                        <span className="text-[8px] opacity-60 font-bold">{copy.previewNote}</span>
                    </button>
                    <button onClick={handleExportPdf} className="w-full py-3 lg:py-5 bg-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-blue-700 shadow-xl transition-all">
                        {isSaving ? copy.generating : <><Download size={16} /> {copy.exportPdf}</>}
                    </button>
                    <button onClick={() => setActiveDoc(null)} className="col-span-2 w-full py-2 text-slate-400 hover:text-slate-600 text-[10px] font-black uppercase tracking-widest transition-colors">{isEnglishBeta ? '' : '← '}{copy.backToList}</button>
                </div>
            </div>

            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                {/* 🚀 1. 最外枠：ここ自体はスクロールさせず、高さを管理するだけ */}
                <div className={`${editMode === 'visual' ? 'h-[60%]' : 'h-1/2'} relative border-b border-slate-300 bg-slate-200 transition-all duration-500 ease-in-out overflow-hidden flex-shrink-0`}>
                    
                    {/* 🚀 2. 浮いているパネル：z-20で最前面に固定（ここが言いたいことの核心！） */}
                    <div className="absolute top-3 left-3 sm:top-6 sm:left-8 z-20 flex flex-wrap items-center gap-2 pointer-events-none">
                        <div className="pointer-events-auto bg-white/90 backdrop-blur px-5 py-2 rounded-full text-[10px] font-black text-slate-600 shadow-xl border border-slate-100 flex items-center gap-3">
                            <Eye size={14} className="text-blue-500"/> {copy.viewingPage} {selectedPageIndex + 1}
                        </div>
                        <div className="pointer-events-auto bg-white/90 backdrop-blur p-1 rounded-full shadow-xl border border-slate-100 flex items-center gap-1">
                            <button onClick={handleZoomOut} className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400"><ZoomOut size={14}/></button>
                            <span className="text-[9px] font-black text-slate-400 w-10 text-center">{Math.round(previewScale * 100)}%</span>
                            <button onClick={handleZoomIn} className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400"><ZoomIn size={14}/></button>
                            <div className="w-[1px] h-3 bg-slate-200 mx-1" />
                            <button onClick={handleZoomFit} className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400"><Maximize size={14}/></button>
                        </div>
                    </div>

                    {/* 🚀 3. スクロール専用エリア：スライドだけがこの中で動く */}
                    <div ref={previewContainerRef} className="absolute inset-0 p-8 sm:p-14 lg:p-20 flex overflow-auto">
                        <div style={{ margin: 'auto', width: `${1122 * previewScale}px`, height: `${793 * previewScale}px`, position: 'relative', flexShrink: 0 }}>
                            <div style={{ width: '1122px', height: '793px', transform: `scale(${previewScale})`, transformOrigin: '0 0', position: 'absolute' }} className="bg-white shadow-2xl rounded-sm overflow-hidden ring-8 ring-white/20">
                                {memoizedIframe}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 bg-white flex flex-col min-h-0">
                    <div className="px-3 sm:px-6 lg:px-10 py-4 lg:py-5 border-b flex flex-col gap-3 xl:flex-row xl:justify-between xl:items-center bg-white shadow-sm relative z-10 flex-shrink-0">
                        <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                            <div className="flex bg-slate-100 p-1 rounded-xl">
                                <button 
                                    onClick={() => setEditMode('code')}
                                    className={`px-4 py-1.5 rounded-lg text-[10px] font-black transition-all ${editMode === 'code' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                                >CODE</button>
                                <button 
                                    onClick={() => setEditMode('visual')}
                                    className={`px-4 py-1.5 rounded-lg text-[10px] font-black transition-all ${editMode === 'visual' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                                >VISUAL (BETA)</button>
                            </div>
                            {isSaving && <span className="text-[10px] text-blue-600 font-bold animate-pulse uppercase tracking-widest">Autosaving...</span>}
                        </div>
                        {validateHtmlContent(pages[selectedPageIndex]?.raw_html, selectedPageIndex + 1, isEnglishBeta) && (
                            <div className="absolute top-16 left-10 right-10 z-20 space-y-1">
                                {validateHtmlContent(pages[selectedPageIndex]?.raw_html, selectedPageIndex + 1, isEnglishBeta).map((msg, i) => (
                                    <div key={i} className="bg-red-500 text-white text-[10px] font-black p-2 rounded-lg shadow-xl flex items-center gap-2 animate-bounce">
                                        <AlertTriangle size={14} /> {msg}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                            {/* 🚀 VISUALモード専用の書式編集ボタン */}
                            {editMode === 'visual' && (
                                <div className="flex max-w-full flex-wrap items-center gap-1 mr-0 sm:mr-4 bg-slate-100 p-1.5 rounded-2xl border border-slate-200">
                                    {/* 太字ボタン */}
                                    <button 
                                        onClick={() => {
                                            const frame = document.querySelector('iframe[title="Main Slide Preview"]');
                                            frame.contentWindow.postMessage({ type: 'EXEC_COMMAND', command: 'bold' }, '*');
                                        }} 
                                        className="w-10 h-10 flex items-center justify-center hover:bg-white hover:shadow-sm rounded-xl text-slate-800 transition-all active:scale-95"
                                        title={copy.bold}
                                    >
                                        <Bold size={18} />
                                    </button>
                                    
                                    <div className="w-[1px] h-6 bg-slate-200 mx-1" />

                                    {/* 文字色ボタン */}
                                    <div className="relative group flex items-center justify-center w-10 h-10 hover:bg-white hover:shadow-sm rounded-xl transition-all cursor-pointer">
                                        <input 
                                            type="color" 
                                            onChange={(e) => {
                                                const frame = document.querySelector('iframe[title="Main Slide Preview"]');
                                                frame.contentWindow.postMessage({ type: 'EXEC_COMMAND', command: 'foreColor', value: e.target.value }, '*');
                                            }}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        />
                                        <div className="flex flex-col items-center">
                                            <span className="text-[10px] font-black leading-none">A</span>
                                            <div className="w-4 h-1 bg-red-500 mt-0.5 rounded-full" />
                                        </div>
                                    </div>

                                    {/* フォントサイズ */}
                                    <select 
                                        onChange={(e) => {
                                            const frame = document.querySelector('iframe[title="Main Slide Preview"]');
                                            frame.contentWindow.postMessage({ type: 'EXEC_COMMAND', command: 'fontSize', value: e.target.value }, '*');
                                        }}
                                        className="bg-transparent text-[10px] font-black px-2 py-2 outline-none border-l border-slate-200 cursor-pointer"
                                    >
                                        <option value="3">{copy.sizeDefault}</option>
                                        <option value="1">{copy.sizeTiny}</option>
                                        <option value="2">{copy.sizeSmall}</option>
                                        <option value="4">{copy.sizeLarge}</option>
                                        <option value="5">{copy.sizeXLarge}</option>
                                        <option value="6">{copy.sizeHuge}</option>
                                        <option value="7">{copy.sizeMax}</option>
                                    </select>
                                </div>
                            )}
                            <button 
                                onClick={handleManualSave} 
                                className="px-4 sm:px-6 py-2.5 bg-blue-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg flex items-center gap-2 active:scale-95"
                            >
                                <Save size={14}/> {isSaving ? copy.saving : copy.save}
                            </button>
                            <button onClick={() => setShowMediaModal(true)} className="px-4 sm:px-6 py-2.5 bg-slate-50 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all border border-slate-200 flex items-center gap-2 active:scale-95"><ImageIcon size={14}/> {copy.media}</button>
                            {!isEnglishBeta && (
                                <>
                                    <button onClick={() => setShowCorrectionModal(true)} className="px-4 sm:px-6 py-2.5 bg-amber-50 text-amber-700 rounded-full text-[10px] font-black flex items-center gap-2 hover:bg-amber-100 transition-all border border-amber-200 active:scale-95"><Eraser size={14}/> {copy.correctionAi}</button>
                                    <a href={GEM_URLS.CRAFTSMAN} target="_blank" rel="noopener noreferrer" className="px-4 sm:px-6 py-2.5 bg-slate-50 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all border border-slate-200 flex items-center gap-2 active:scale-95"><Sparkles size={14}/> {copy.generateAi}</a>
                                    <button onClick={() => setShowPromptModal(true)} className="px-5 sm:px-8 py-2.5 bg-indigo-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all flex items-center gap-2 active:scale-95"><Wand2 size={14}/> {copy.firstAi}</button>
                                </>
                            )}
                        </div>
                    </div>
                    {editMode === 'code' ? (
                        <textarea 
                            className="flex-1 w-full p-4 sm:p-8 lg:p-12 font-mono text-[11px] text-slate-600 outline-none resize-none bg-slate-50/10 leading-loose scrollbar-hide"
                            placeholder={copy.codePlaceholder}
                            value={pages[selectedPageIndex]?.raw_html || ""}
                            onChange={(e) => {
                                const newPages = [...pages];
                                if(newPages[selectedPageIndex]) {
                                    newPages[selectedPageIndex].raw_html = e.target.value;
                                    setPages(newPages);
                                }
                            }}
                            onBlur={(e) => handleSavePageContent(e.target.value)}
                        />
                    ) : (
                        <div className="flex-1 overflow-y-auto min-h-0 bg-slate-50/30">
                            {extractedChartData ? (
                                <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto w-full pb-40">
                                    <div className="flex items-center justify-between mb-8 sm:mb-10">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-blue-100">
                                                <BarChart3 size={24}/>
                                            </div>
                                            <div>
                                                <h4 className="font-black text-slate-800 text-lg">{copy.chartEditorTitle}</h4>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Master Logic Editor</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 🚀 カテゴリラベル（横軸） */}
                                    <div className="mb-8 sm:mb-12 bg-white p-5 sm:p-8 rounded-3xl sm:rounded-[2.5rem] border border-slate-100 shadow-sm">
                                        <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                                            {copy.xAxisEditor}
                                        </h5>
                                        <div className="flex flex-wrap gap-3">
                                            {extractedChartData.labels.map((label, lIdx) => (
                                                <input 
                                                    key={lIdx}
                                                    type="text"
                                                    value={label}
                                                    onChange={(e) => handleUpdateXLabel(lIdx, e.target.value)}
                                                    className="bg-slate-50 border-none rounded-full px-4 py-2 text-xs font-bold text-slate-600 focus:ring-2 focus:ring-blue-500 outline-none w-24 text-center transition-all"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* 🚀 各データセットのグリッド編集 */}
                                    <div className="space-y-12">
                                        {extractedChartData.datasets.map((dataset, dIdx) => (
                                            <div key={dIdx} className="bg-white p-5 sm:p-8 rounded-3xl sm:rounded-[2.5rem] border border-slate-100 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                                                <div className="flex items-center gap-4 mb-6">
                                                    <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
                                                    <input 
                                                        type="text"
                                                        value={dataset.label}
                                                        readOnly={dataset.type === 'variable'}
                                                        onChange={(e) => handleUpdateDatasetLabel(dIdx, e.target.value)}
                                                        className={`text-sm font-black text-slate-800 border-none rounded-lg px-3 py-1 outline-none ${dataset.type === 'variable' ? 'bg-slate-50 text-slate-400' : 'bg-blue-50/50 focus:ring-2 focus:ring-blue-500'}`}
                                                        placeholder={copy.itemPlaceholder}
                                                    />
                                                    {dataset.type === 'variable' && <span className="text-[9px] font-bold text-slate-300 uppercase tracking-tighter">Code Variable</span>}
                                                </div>
                                                
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                                                    {extractedChartData.labels.map((label, lIdx) => (
                                                        <div key={lIdx} className="bg-slate-50/50 p-4 rounded-[1.5rem] border border-transparent hover:border-blue-200 transition-all">
                                                            <div className="text-[9px] font-black text-slate-400 mb-2 truncate">{label}</div>
                                                            <input 
                                                                type="number" 
                                                                value={dataset.values[lIdx] || 0}
                                                                onChange={(e) => handleUpdateChartValue(dIdx, lIdx, e.target.value)}
                                                                className="w-full bg-white border-none rounded-xl p-2.5 text-right font-mono text-sm font-bold text-slate-700 shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="mt-12 sm:mt-16 p-5 sm:p-8 bg-slate-900 rounded-3xl sm:rounded-[2.5rem] shadow-2xl flex items-start gap-4 sm:gap-6 relative overflow-hidden">
                                        <Sparkles className="text-blue-400 shrink-0 mt-1" size={24}/>
                                        <div className="relative z-10">
                                            <p className="text-sm text-white leading-relaxed font-bold">
                                                {copy.syncSuccess}
                                            </p>
                                            <p className="text-[11px] text-slate-400 mt-2">
                                                {copy.syncNote}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-slate-400 p-20 text-center">
                                    <Sparkles className="mb-6 text-blue-500 opacity-20" size={60} />
                                    <h4 className="font-black text-slate-600 mb-4 text-lg tracking-tight">{copy.visualTitle}</h4>
                                    <div className="space-y-4">
                                        <p className="text-sm leading-relaxed max-w-sm text-slate-400 font-medium">
                                            {copy.visualDescription}
                                        </p>
                                        <div className="bg-amber-50/50 border border-amber-100 p-6 rounded-[2rem] inline-block shadow-sm">
                                            <p className="text-xs font-black text-amber-600 flex items-center gap-2 justify-center">
                                                <AlertTriangle size={14}/> {copy.visualWarning}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {!isEnglishBeta && showPromptModal && <PresentationPromptModal activeDoc={activeDoc} businessInfo={businessInfo} images={images} onClose={() => setShowPromptModal(false)} />}
            {showMediaModal && <PresentationMediaModal docId={activeDoc.id} images={images} onRefresh={() => fetchPagesAndImages(activeDoc.id)} onClose={() => setShowMediaModal(false)} isEnglishBeta={isEnglishBeta} />}
            {!isEnglishBeta && showCorrectionModal && <CorrectionPromptModal currentHtml={pages[selectedPageIndex]?.raw_html || ""} onClose={() => setShowCorrectionModal(false)} />}
        </div>
    );
}
